import React, { useEffect } from 'react'
import styled from 'styled-components'
import { FadeInUp, FadeInLeft, FadeInRight, StaggerContainer, StaggerItem, AnimatedCounter, HoverCard } from '../Components/Animations'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  primaryDark: '#0f766e',
  secondary: '#0891b2',
  accent: '#5eead4',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc',
  backgroundAlt: '#f0fdfa'
}

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${colors.white};
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

// Hero Section
const HeroSection = styled.section`
  padding: 160px 0 80px;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(8, 145, 178, 0.15) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 120px 0 60px;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const HeroLabel = styled.span`
  display: inline-block;
  background: rgba(20, 184, 166, 0.2);
  color: ${colors.accent};
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;
`

const HeroTitle = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 24px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

// Stats Section
const StatsSection = styled.section`
  padding: 0;
  margin-top: -60px;
  position: relative;
  z-index: 10;
`

const StatsCard = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 32px;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const StatItem = styled.div`
  text-align: center;
  padding: 20px;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.backgroundAlt};
  }
`

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 8px;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const StatLabel = styled.div`
  font-size: 16px;
  color: ${colors.textLight};
  font-weight: 500;
`

// Section Styles
const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 60px;
`

const SectionLabel = styled.span`
  display: inline-block;
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: ${colors.dark};
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${colors.textLight};
  line-height: 1.7;
`

// Mission Section
const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const MissionCard = styled.div`
  background: ${colors.white};
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.primary}, ${colors.secondary});
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`

const MissionIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 28px;
`

const MissionTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 16px;
`

const MissionText = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${colors.textLight};
`

// Values Section
const ValuesSection = styled.section`
  padding: 100px 0;
  background: ${colors.backgroundAlt};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ValueCard = styled.div`
  background: ${colors.white};
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  display: flex;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  }
`

const ValueIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
`

const ValueContent = styled.div`
  flex: 1;
`

const ValueTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 10px;
`

const ValueText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.textLight};
`

// Offices Section
const OfficesSection = styled.section`
  padding: 100px 0;
  background: ${colors.white};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const OfficesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const OfficeCard = styled.div`
  background: ${colors.background};
  padding: 40px;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${colors.primary}, ${colors.secondary});
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  }
`

const OfficeFlag = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
`

const OfficeTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 16px;
`

const OfficeAddress = styled.p`
  font-size: 15px;
  line-height: 1.8;
  color: ${colors.textLight};
`

// CTA Section
const CTASection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 70%, rgba(20, 184, 166, 0.2) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
`

const CTATitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const CTAText = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  line-height: 1.7;
`

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  padding: 16px 40px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 40px rgba(20, 184, 166, 0.3);
  }
`

const RSNGAbout = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const stats = [
    { number: 150, suffix: '+', label: 'Talented People' },
    { number: 5, suffix: '+', label: 'Years of Experience' },
    { number: 100, suffix: '+', label: 'Projects Delivered' },
    { number: 10, suffix: '+', label: 'Technologies' }
  ]

  const missionItems = [
    {
      icon: '🎯',
      title: 'Our Mission',
      text: 'Deliver customizable, error-free software solutions that are cost-effective for modern business and marketing needs. We are committed to providing quality solutions that drive business success.'
    },
    {
      icon: '🔭',
      title: 'Our Vision',
      text: 'To be the most trusted technology partner for businesses worldwide, enabling digital transformation through innovative solutions and exceptional service delivery.'
    },
    {
      icon: '📋',
      title: 'Our Strategy',
      text: 'We analyze requirements thoroughly to present efficient action plans, combining unique designs with technical services to ensure seamless business operations tailored to each client.'
    }
  ]

  const coreValues = [
    {
      icon: '✓',
      title: 'Accountability',
      description: 'We stand behind our work and respond promptly to client needs, building lasting trust through complete ownership of outcomes.'
    },
    {
      icon: '⚖',
      title: 'Integrity',
      description: 'Every decision we make is guided by strong moral principles, ensuring ethical business practices that honor our commitments.'
    },
    {
      icon: '★',
      title: 'Responsibility',
      description: 'We deliver with uncompromising quality, understanding the critical impact our solutions have on your business success.'
    },
    {
      icon: '💡',
      title: 'Creativity',
      description: 'We solve problems with fresh perspectives, crafting innovative solutions that set your business apart from competitors.'
    },
    {
      icon: '🤝',
      title: 'Honesty',
      description: 'We maintain transparent communication and honor the trust our clients place in us, ensuring genuine partnerships.'
    }
  ]

  const offices = [
    {
      flag: '🇮🇳',
      title: 'Hyderabad, India',
      address: '605 B Block, Asian Sun City\nBackside of Sharath City Capital Mall\nKondapur 500084\nHyderabad, India'
    },
    {
      flag: '🇮🇳',
      title: 'Visakhapatnam, India',
      address: '10 A (Part), SDF-1 Building, Phase-I\nVSEZ, Duvvada\nVisakhapatnam, AP 530049\nIndia'
    },
    {
      flag: '🇺🇸',
      title: 'Wyoming, USA',
      address: '1309 Coffeen AVE STE 1200\nSheridan, WY 82801\nUnited States'
    }
  ]

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <FadeInUp>
              <HeroLabel>About Us</HeroLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <HeroTitle>Transforming Ideas Into Digital Reality</HeroTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <HeroSubtitle>
                We are a group of thinkers, designers, writers, and engineers who transform product ideas from zero to one and beyond. Distinguished by our tech experience and results-driven methodology, we partner with clients to solve their biggest challenges.
              </HeroSubtitle>
            </FadeInUp>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      {/* Stats Section */}
      <StatsSection>
        <MaxWidthContainer>
          <FadeInUp>
            <StatsCard>
              {stats.map((stat, index) => (
                <StatItem key={index}>
                  <StatNumber>
                    <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                  </StatNumber>
                  <StatLabel>{stat.label}</StatLabel>
                </StatItem>
              ))}
            </StatsCard>
          </FadeInUp>
        </MaxWidthContainer>
      </StatsSection>

      {/* Mission Section */}
      <Section>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>Who We Are</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionTitle>Our Mission, Vision & Strategy</SectionTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <SectionSubtitle>
                At RSNG INFO, we prioritize customer needs and create quality solutions that meet client requirements through collaboration and expertise.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <StaggerContainer staggerDelay={0.15}>
            <MissionGrid>
              {missionItems.map((item, index) => (
                <StaggerItem key={index}>
                  <HoverCard>
                    <MissionCard>
                      <MissionIcon>{item.icon}</MissionIcon>
                      <MissionTitle>{item.title}</MissionTitle>
                      <MissionText>{item.text}</MissionText>
                    </MissionCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </MissionGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </Section>

      {/* Values Section */}
      <ValuesSection>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>Our Foundation</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionTitle>Core Values That Define Us</SectionTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <SectionSubtitle>
                Our foundation is built on unwavering principles that guide every decision, project, and client relationship we nurture.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <StaggerContainer staggerDelay={0.1}>
            <ValuesGrid>
              {coreValues.map((value, index) => (
                <StaggerItem key={index}>
                  <ValueCard>
                    <ValueIcon>{value.icon}</ValueIcon>
                    <ValueContent>
                      <ValueTitle>{value.title}</ValueTitle>
                      <ValueText>{value.description}</ValueText>
                    </ValueContent>
                  </ValueCard>
                </StaggerItem>
              ))}
            </ValuesGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </ValuesSection>

      {/* Offices Section */}
      <OfficesSection>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>Global Presence</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionTitle>Our Office Locations</SectionTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <SectionSubtitle>
                We serve clients globally from our strategically located offices across India and the United States.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <StaggerContainer staggerDelay={0.15}>
            <OfficesGrid>
              {offices.map((office, index) => (
                <StaggerItem key={index}>
                  <HoverCard>
                    <OfficeCard>
                      <OfficeFlag>{office.flag}</OfficeFlag>
                      <OfficeTitle>{office.title}</OfficeTitle>
                      <OfficeAddress>
                        {office.address.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < office.address.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </OfficeAddress>
                    </OfficeCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </OfficesGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </OfficesSection>

      {/* CTA Section */}
      <CTASection>
        <MaxWidthContainer>
          <CTAContent>
            <FadeInUp>
              <CTATitle>Ready to Start Your Project?</CTATitle>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <CTAText>
                Let's discuss how we can help transform your ideas into reality. Our team is ready to bring your vision to life.
              </CTAText>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <CTAButton href="/contact">
                Get In Touch
                <span>→</span>
              </CTAButton>
            </FadeInUp>
          </CTAContent>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default RSNGAbout
